/**
 * Testimonials manifest.
 *
 * ⚠️ EVERY ENTRY HERE IS CURRENTLY A PLACEHOLDER. Replace each one with a
 * real quote — and flip `is_placeholder` to false — as soon as you have
 * the customer's written approval. The TestimonialGrid component shows a
 * dev-only banner whenever placeholders are still rendered so you can't
 * accidentally ship the launch announcement with sample data.
 *
 * To add a real testimonial:
 *   1. Get the customer's signed-off quote (email or DM is fine)
 *   2. Replace the matching placeholder OR add a new entry below
 *   3. Set `is_placeholder: false`
 *   4. (Optional) drop an avatar image into /public/testimonials/{slug}.jpg
 *      and set `avatar_url: '/testimonials/{slug}.jpg'`. Otherwise the
 *      initials avatar renders.
 *
 * Shape decisions:
 *   - quote stays under ~280 chars — anything longer doesn't fit on mobile
 *   - role + agency together give social proof without disclosing
 *     anything the customer didn't want public
 *   - vertical tag enables filtering per landing page
 */
import type { BlogPostMeta } from '~/utils/blogPosts'

export interface Testimonial {
  slug: string
  quote: string
  /** Customer's name. */
  name: string
  /** Their role at the agency. */
  role: string
  /** The agency's display name. */
  agency: string
  /** Which vertical it belongs to — matches blog post vertical keys. */
  vertical: BlogPostMeta['vertical']
  /** City + country, e.g. "Sydney, AU". */
  location?: string
  /** /public-rooted avatar path. Falls back to initials. */
  avatar_url?: string
  /** Flip to false when this is a real, approved testimonial. */
  is_placeholder: boolean
  /** Optional: emphasize on the homepage (featured = 3 across all verticals). */
  featured?: boolean
}

export const TESTIMONIALS: Testimonial[] = [
  // ── Law firms ────────────────────────────────────────────────
  {
    slug: 'tsatsral-partners-law',
    quote:
      "The conflict check used to be me asking my partner if a name rang a bell. Now it's a three-second search across every matter we've ever opened — and the trust ledger gives me a client statement in one click instead of one evening.",
    name: 'Sample Name',
    role: 'Managing Partner',
    agency: 'Tsatsral & Partners (sample)',
    vertical: 'law-firms',
    location: 'Ulaanbaatar, MN',
    is_placeholder: true,
    featured: true,
  }
]

export const testimonialsForVertical = (vertical: Testimonial['vertical']) =>
  TESTIMONIALS.filter(t => t.vertical === vertical)

export const featuredTestimonials = () =>
  TESTIMONIALS.filter(t => t.featured)

/** Cheap initials extraction for avatar fallback. */
export const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || '?'
}

/** Is the current dev environment showing placeholder data anywhere? */
export const hasPlaceholders = (subset: Testimonial[] = TESTIMONIALS): boolean =>
  subset.some(t => t.is_placeholder)
